import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Button,
  DialogActions,
  Tooltip,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";
import FilterInvoice from "./InvoiceForm/FilterInvoice";
import InvoiceDocuments from "./InvoiceForm//InvoiceDocuments";
import xmlIcon from "../../static/media/icon/xml.svg";

const getCookie = (name) => {
  const v = `; ${document.cookie}`;
  const parts = v.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return "";
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) => `$${toNum(v).toFixed(2)}`;

const AdminInvoice = () => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");

  const [origenFilter, setOrigenFilter] = useState("TODOS");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const [loadingModal, setLoadingModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [confirmAnular, setConfirmAnular] = useState({
    open: false,
    doc: null,
  });

  const [downloadModal, setDownloadModal] = useState({
    open: false,
    title: "Descargando reporte",
    message: "Generando el documento…",
  });

  const fetchDocs = async () => {
    try {
      const r = await fetch("/facturas/documentos/listar/", { credentials: "include" });
      const d = await r.json().catch(() => ({}));
      setDocs(d.documentos || []);
    } catch {
      setDocs([]);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [search, origenFilter, dateFrom, dateTo]);

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  const labelDocumento = (d) => {
    if (d.tipo_documento === "01") return "FAC";
    if (d.tipo_documento === "04") return "NC";
    if (d.tipo_documento === "05") return "ND";
    if (d.tipo_documento === "06") return "GR";
    if (d.tipo_documento === "07") return "RET";
    return "FAC";
  };

  const rolDocumento = (d) => (d.origen === "COMPRA" ? "Proveedor" : "Cliente");

  const isOk = (estado) => {
    const e = String(estado || "").toUpperCase().trim();
    return e === "AUTORIZADO" || e === "CONFIRMADO" || e === "PAGADA";
  };

  const isAnulada = (estado) => {
    const e = String(estado || "").toUpperCase().trim();
    return e === "ANULADA" || e === "ANULADO";
  };

  const EstadoIcon = ({ estado }) => {
    if (isOk(estado)) return <CheckCircleIcon sx={{ color: "green" }} fontSize="small" />;
    if (isAnulada(estado)) return <CancelIcon sx={{ color: "red" }} fontSize="small" />;
    return null;
  };

  const openMenu = (e, d) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDoc(d);
    setAnchorEl(e.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };

  const anularCompra = async () => {
    if (!selectedDoc) return;
    try {
      const csrftoken = getCookie("csrftoken");

      setLoadingModal({
        open: true,
        title: "Anulando documento",
        message: "Procesando anulación…",
      });

      const r = await fetch("/documentos/compras/anular/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ compra_id: selectedDoc.id }),
      });

      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Respuesta no válida del servidor");

      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || "No se pudo anular");

      setDocs((prev) => prev.map((x) => (x.id === selectedDoc.id ? { ...x, estado: "ANULADA" } : x)));

      setSnackbar({ open: true, message: "Documento anulado correctamente", type: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Error al anular", type: "error" });
    } finally {
      setLoadingModal({ open: false, title: "", message: "" });
      closeMenu();
    }
  };

  const reenviarSri = async () => {
    if (!selectedDoc) return;
    try {
      const csrftoken = getCookie("csrftoken");

      setLoadingModal({
        open: true,
        title: "Reintentando envío al SRI",
        message: "Enviando y consultando autorización…",
      });

      const r = await fetch("/facturas/documentos/reenviar/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ documento_id: selectedDoc.id }),
      });

      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Respuesta no válida del servidor");

      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || "No se pudo reenviar al SRI");

      const nuevoEstado = (data.sri_estado || data.estado || "").trim();

      setDocs((prev) => prev.map((x) => (x.id === selectedDoc.id ? { ...x, estado: nuevoEstado } : x)));

      setSnackbar({
        open: true,
        message:
          String(nuevoEstado).toUpperCase() === "AUTORIZADO"
            ? "Factura autorizada por el SRI"
            : "Reintento enviado. Revisa el estado.",
        type: String(nuevoEstado).toUpperCase() === "AUTORIZADO" ? "success" : "info",
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Error al reenviar", type: "error" });
    } finally {
      setLoadingModal({ open: false, title: "", message: "" });
      closeMenu();
    }
  };

  const anularFactura = async (docToCancel) => {
    if (!docToCancel) return;
    try {
      const csrftoken = getCookie("csrftoken");

      setLoadingModal({
        open: true,
        title: "Anulando factura",
        message: "Revirtiendo stock y marcando como ANULADA…",
      });

      const r = await fetch("/facturas/documentos/anular/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ documento_id: docToCancel.id }),
      });

      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Respuesta no válida del servidor");

      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || "No se pudo anular la factura");

      setDocs((prev) => prev.map((x) => (x.id === docToCancel.id ? { ...x, estado: "ANULADA" } : x)));

      setSnackbar({ open: true, message: "Factura anulada correctamente", type: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Error al anular factura", type: "error" });
    } finally {
      setLoadingModal({ open: false, title: "", message: "" });
      setConfirmAnular({ open: false, doc: null });
      closeMenu();
    }
  };

  const netoDoc = (d) => {
    if (d.neto !== undefined) return d.neto;
    const s15 = toNum(d.subtotal_15);
    const s5 = toNum(d.subtotal_5);
    const s0 = toNum(d.subtotal_0);
    const desc = toNum(d.descuento_total);
    const base = s15 + s5 + s0;
    return base - desc;
  };

  const impDoc = (d) => {
    if (d.impuesto !== undefined) return d.impuesto;
    const i15 = toNum(d.iva_15);
    const i5 = toNum(d.iva_5);
    const iva = toNum(d.iva);
    return i15 + i5 + iva;
  };

  const retDoc = (d) => {
    if (d.retenciones_total !== undefined) return toNum(d.retenciones_total);
    if (d.retencion_total !== undefined) return toNum(d.retencion_total);
    if (d.retenciones !== undefined) return toNum(d.retenciones);
    return 0;
  };

  const puedeAcciones = (d) => {
    const noAnulada = !isAnulada(d.estado);
    if (d.origen === "COMPRA") return noAnulada;
    if (d.origen === "VENTA") return noAnulada;
    return false;
  };

  const startDownload = async () => {
    const { ok, error } = InvoiceDocuments.validateRangeRequired({
      dateFrom,
      dateTo,
    });

    if (!ok) {
      setSnackbar({ open: true, message: error, type: "warning" });
      return;
    }

    setDownloadModal({ open: true, title: "Descargando reporte", message: "Generando el documento…" });

    const params = new URLSearchParams({
      origen: origenFilter,
      search,
      date_from: dateFrom,
      date_to: dateTo,
    });

    const url = `/facturas/documentos/excel/?${params.toString()}`;

    try {
      await InvoiceDocuments.downloadFile(url);
      await InvoiceDocuments.delay(3000);
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || "Error al descargar", type: "error" });
    } finally {
      setDownloadModal((s) => ({ ...s, open: false }));
    }
  };

  const filtered = useMemo(() => {
    const t = search.toLowerCase().trim();

    return (docs || []).filter((d) => {
      const origen = String(d.origen || "").toUpperCase();

      let okOrigen = true;
      switch (String(origenFilter || "TODOS").toUpperCase()) {
        case "VENTA":
          okOrigen = origen === "VENTA";
          break;
        case "COMPRA":
          okOrigen = origen === "COMPRA";
          break;
        default:
          okOrigen = true;
      }

      const okSearch =
        !t ||
        String(d.persona || "").toLowerCase().includes(t) ||
        String(d.numero_documento || "").toLowerCase().includes(t) ||
        String(d.codigo || "").toLowerCase().includes(t);

      const fecha = String(d.fecha_emision || "");
      const okDateFrom = !dateFrom || (fecha && fecha >= dateFrom);
      const okDateTo = !dateTo || (fecha && fecha <= dateTo);

      return okOrigen && okSearch && okDateFrom && okDateTo;
    });
  }, [docs, search, origenFilter, dateFrom, dateTo]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>Documentos electrónicos</Typography>

              <Tooltip title="Descargar reporte excel" arrow>
                <IconButton onClick={startDownload} sx={{ p: 0.5 }}>
                  <img src={xmlIcon} alt="Descargar reporte excel" style={{ width: 28, height: 28 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <FilterInvoice
              search={search}
              setSearch={setSearch}
              origenFilter={origenFilter}
              setOrigenFilter={setOrigenFilter}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
            />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Emisión</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Persona</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Documento</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff", width: 80 }} align="center">
                      Estado
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="right">
                      Neto
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="right">
                      Imp.
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="right">
                      Total
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="right">
                      Ret.
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="right">
                      Saldo
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff", width: 90 }} align="center">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell>{d.fecha_emision}</TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>{d.persona}</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <PersonOutlineIcon sx={{ fontSize: 14, color: "#9e9e9e" }} />
                            <Typography sx={{ fontSize: 11, color: "#9e9e9e" }}>{rolDocumento(d)}</Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Link
                            href={`/admin-panel/factura/${d.id}`}
                            target="_blank"
                            underline="hover"
                            sx={{ fontWeight: 600, display: "block" }}
                          >
                            {labelDocumento(d)} {d.numero_documento}
                          </Link>
                          <Typography sx={{ fontSize: 11, color: "#9e9e9e" }}>
                            {d.origen === "COMPRA" ? "Compra" : "Venta"}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <EstadoIcon estado={d.estado} />
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {money(netoDoc(d))}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {money(impDoc(d))}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {money(d.total)}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {money(retDoc(d))}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {money(d.saldo)}
                        </TableCell>

                        <TableCell align="center">
                          {puedeAcciones(d) && (
                            <IconButton size="small" onClick={(e) => openMenu(e, d)} sx={{ color: "#1565C0" }}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        No hay documentos registrados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, n) => setPage(n)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {selectedDoc?.origen === "VENTA" && !isAnulada(selectedDoc?.estado) && !isOk(selectedDoc?.estado) && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              reenviarSri();
            }}
            sx={{ fontWeight: 600 }}
          >
            Reintentar envío al SRI
          </MenuItem>
        )}

        {selectedDoc?.origen === "VENTA" && !isAnulada(selectedDoc?.estado) && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setConfirmAnular({ open: true, doc: selectedDoc });
            }}
            sx={{ fontWeight: 600, color: "#d32f2f" }}
          >
            Anular Factura
          </MenuItem>
        )}

        {selectedDoc?.origen === "COMPRA" && !isAnulada(selectedDoc?.estado) && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              anularCompra();
            }}
            sx={{ fontWeight: 600 }}
          >
            Anular Documento
          </MenuItem>
        )}
      </Menu>

      <Dialog open={loadingModal.open} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{loadingModal.title}</DialogTitle>
        <DialogContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <CircularProgress />
          <Typography sx={{ fontWeight: 600 }}>{loadingModal.message}</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={downloadModal.open} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{downloadModal.title}</DialogTitle>
        <DialogContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <CircularProgress />
          <Typography sx={{ fontWeight: 600 }}>{downloadModal.message}</Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmAnular.open}
        onClose={() => setConfirmAnular({ open: false, doc: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#d32f2f" }}>Confirmar anulación</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            ¿Seguro deseas anular la factura <b>{confirmAnular.doc?.numero_documento || ""}</b>?
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 12, color: "#757575" }}>
            Esto marcará la factura como ANULADA y revertirá el stock (según tu backend).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmAnular({ open: false, doc: null })} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={() => anularFactura(confirmAnular.doc)}
            variant="contained"
            sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#b71c1c" } }}
          >
            Anular
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminInvoice;
